import { useNavigate } from "@tanstack/solid-router";
import { createMutation, createQuery, useQueryClient } from "@tanstack/solid-query";
import { For, Show, createSignal } from "solid-js";
import { createOpportunity, fetchSkills, type OpportunityType } from "~/lib/api";
import { SKILL_CATEGORY_LABELS, skillsByCategory } from "~/lib/skills";

const MAX_TITLE = 120;
const MAX_DESCRIPTION = 3000;
const MAX_LOCATION = 100;
const MAX_SKILLS = 15;

const TYPES: { id: OpportunityType; label: string }[] = [
  { id: "job", label: "Job" },
  { id: "gig", label: "Gig" },
  { id: "mentorship", label: "Mentorship" },
  { id: "collab", label: "Collab" },
  { id: "speaking", label: "Speaking" },
];

type FormValues = {
  type: OpportunityType;
  title: string;
  description: string;
  location: string;
  remoteOk: boolean;
  applyUrl: string;
  expiresAt: string;
  skillIds: string[];
};

export function EditOpportunityPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [values, setValues] = createSignal<FormValues>({
    type: "job",
    title: "",
    description: "",
    location: "",
    remoteOk: false,
    applyUrl: "",
    expiresAt: "",
    skillIds: [],
  });

  const skillsQuery = createQuery(() => ({
    queryKey: ["skills"],
    queryFn: fetchSkills,
  }));

  const saveMutation = createMutation(() => ({
    mutationFn: () => {
      const v = values();
      return createOpportunity({
        type: v.type,
        title: v.title.trim(),
        description: v.description.trim(),
        location: v.location.trim() || undefined,
        remote_ok: v.remoteOk,
        apply_url: v.applyUrl.trim() || undefined,
        expires_at: v.expiresAt ? new Date(`${v.expiresAt}T23:59:59`).toISOString() : undefined,
        skill_tag_ids: v.skillIds,
      });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["opportunities"] });
      navigate({ to: "/opportunities/$opportunityId", params: { opportunityId: res.data.id } });
    },
  }));

  function toggleSkill(id: string) {
    setValues((prev) => ({
      ...prev,
      skillIds: prev.skillIds.includes(id)
        ? prev.skillIds.filter((x) => x !== id)
        : prev.skillIds.length < MAX_SKILLS
          ? [...prev.skillIds, id]
          : prev.skillIds,
    }));
  }

  return (
    <section class="page">
      <div class="page-title-block" style={{ padding: "20px 20px 0" }}>
        <p class="status-chip">Board</p>
        <h1>Post an opportunity</h1>
        <p class="muted">Jobs, gigs, mentorship, and speaking — visible to claimed DEB members.</p>
      </div>

      <div class="card" style={{ margin: "16px 20px" }}>
        <form
          class="wizard-form"
          onSubmit={(e) => {
            e.preventDefault();
            saveMutation.mutate();
          }}
        >
          <div class="field">
            <span>Type</span>
            <div class="interest-grid" style={{ margin: "0" }} role="group" aria-label="Opportunity type">
              <For each={TYPES}>
                {(t) => (
                  <button
                    type="button"
                    class={`interest-chip${values().type === t.id ? " selected" : ""}`}
                    aria-pressed={values().type === t.id}
                    onClick={() => setValues((prev) => ({ ...prev, type: t.id }))}
                  >
                    {t.label}
                  </button>
                )}
              </For>
            </div>
          </div>
          <label class="field">
            <span>Title</span>
            <input
              required
              maxlength={MAX_TITLE}
              value={values().title}
              placeholder="Senior Data Engineer — streaming platform"
              onInput={(e) => setValues((prev) => ({ ...prev, title: e.currentTarget.value }))}
            />
          </label>
          <label class="field">
            <span>Description</span>
            <textarea
              required
              maxlength={MAX_DESCRIPTION}
              rows={6}
              value={values().description}
              placeholder="What you’re looking for, and who should reach out."
              onInput={(e) => setValues((prev) => ({ ...prev, description: e.currentTarget.value }))}
            />
          </label>
          <label class="field">
            <span>Location</span>
            <input
              maxlength={MAX_LOCATION}
              value={values().location}
              placeholder="Melbourne (hybrid)"
              onInput={(e) => setValues((prev) => ({ ...prev, location: e.currentTarget.value }))}
            />
          </label>
          <label class="switch-row">
            <span>Remote OK</span>
            <input
              type="checkbox"
              checked={values().remoteOk}
              aria-label="Remote OK"
              onChange={(e) => setValues((prev) => ({ ...prev, remoteOk: e.currentTarget.checked }))}
            />
          </label>
          <label class="field">
            <span>Apply URL</span>
            <input
              type="url"
              value={values().applyUrl}
              placeholder="https://"
              onInput={(e) => setValues((prev) => ({ ...prev, applyUrl: e.currentTarget.value }))}
            />
            <p class="field-hint">Optional. People can also send a Connect request.</p>
          </label>
          <label class="field">
            <span>Expires</span>
            <input
              type="date"
              value={values().expiresAt}
              onInput={(e) => setValues((prev) => ({ ...prev, expiresAt: e.currentTarget.value }))}
            />
            <p class="field-hint">Optional. Hidden from the board after this date.</p>
          </label>

          <Show when={skillsQuery.data} keyed>
            {(payload) => (
              <div>
                <p class="status-chip">Skills</p>
                <p class="field-hint">
                  Up to {MAX_SKILLS}
                  <Show when={values().skillIds.length}> — {values().skillIds.length} selected</Show>
                </p>
                <For each={skillsByCategory(payload.data)}>
                  {([category, tags]) => (
                    <div class="skill-category">
                      <p class="skill-category-label">{SKILL_CATEGORY_LABELS[category] ?? category}</p>
                      <div class="interest-grid" style={{ margin: "0" }}>
                        <For each={tags}>
                          {(tag) => (
                            <button
                              type="button"
                              class={`skill-chip skill-chip--toggle${values().skillIds.includes(tag.id) ? " selected" : ""}`}
                              aria-pressed={values().skillIds.includes(tag.id)}
                              onClick={() => toggleSkill(tag.id)}
                            >
                              {tag.label}
                            </button>
                          )}
                        </For>
                      </div>
                    </div>
                  )}
                </For>
              </div>
            )}
          </Show>

          <Show when={saveMutation.isError}>
            <div class="error-box">{(saveMutation.error as Error).message}</div>
          </Show>

          <div class="wizard-actions">
            <button type="button" class="btn btn-ghost" onClick={() => navigate({ to: "/opportunities" })}>
              Cancel
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              disabled={saveMutation.isPending || !values().title.trim() || !values().description.trim()}
            >
              {saveMutation.isPending ? "Posting…" : "Post opportunity"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
